import { useEffect, useMemo, useState } from 'react';
import { PackagePlus, SlidersHorizontal } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Switch } from '../../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { cn } from '../../../lib/utils';
import {
  billingApi,
  type ContractAccountDetail,
  type ContingentPackage,
  type ContingentSummary,
  type Tariff,
} from '../../../lib/api/billing';
import {
  CONTINGENT_MODE_LABEL,
  LESSON_STATUS_LABEL,
  PACKAGE_SOURCE_LABEL,
  UNIT_ENTRY_KIND_LABEL,
  formatCents,
  formatIsoDate,
  newIdempotencyKey,
  tariffPriceLabel,
  todayIso,
} from '../../../lib/billing';

const NO_FOLLOWUP = 'none';
const ENTRY_PAGE = 30;

interface Props {
  detail: ContractAccountDetail & { contingent: ContingentSummary };
  canEdit: boolean;
  busy: boolean;
  /** Führt eine Aktion aus, die das aktualisierte Vertragskonto liefert; true bei Erfolg. */
  onRun: (action: () => Promise<ContractAccountDetail>, message: string) => Promise<boolean>;
}

function UnitBalance({ units, usageBased }: { units: number; usageBased: boolean }) {
  if (usageBased) {
    return (
      <span className="text-lg font-semibold tabular-nums">
        {Math.max(-units, 0)} <span className="text-sm font-normal text-muted-foreground">insgesamt verbraucht</span>
      </span>
    );
  }
  return (
    <span className={cn('text-lg font-semibold tabular-nums', units < 0 && 'text-destructive', units === 0 && 'text-warning')}>
      {units} {Math.abs(units) === 1 ? 'Einheit' : 'Einheiten'}
      {units < 0 && <span className="ml-1 text-sm font-normal">(ohne Paket verbraucht)</span>}
    </span>
  );
}

/**
 * Kontingent auf dem Vertragskonto (ADR-0003, Phase 5): Einheitenkonto, Pakete mit Raten,
 * Folgepaket, Nachbuchen und Korrektur. Das Geld dazu steht in den Buchungen darunter.
 */
export default function ContingentPanel({ detail, canEdit, busy, onRun }: Props) {
  const contingent = detail.contingent;
  const usageBased = detail.contingentMode === 'usage_based';
  const closed = Boolean(contingent.closedAt);
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [buying, setBuying] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [entryLimit, setEntryLimit] = useState(ENTRY_PAGE);

  useEffect(() => {
    let cancelled = false;
    billingApi
      .getContingent(detail.id)
      .then((response) => {
        if (!cancelled) setTariffs(Array.isArray(response?.availableTariffs) ? response.availableTariffs : []);
      })
      .catch(() => {
        if (!cancelled) setTariffs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [detail.id]);

  const packageTariffs = tariffs.filter((tariff) => tariff.contingentMode !== 'usage_based');
  const followupName =
    tariffs.find((tariff) => tariff.id === contingent.followupTariffId)?.name ??
    contingent.packages.find((pkg) => pkg.tariffId === contingent.followupTariffId)?.name ??
    null;

  const purchasedOn = useMemo(
    () => new Map(contingent.packages.map((pkg) => [pkg.id, pkg.purchasedOn])),
    [contingent.packages],
  );

  // Laufender Stand je Zeile: die Liste ist neueste zuerst, der Stand oben ist der heutige.
  const entries = useMemo(() => {
    let running = contingent.unitBalance;
    return contingent.entries.map((entry) => {
      const row = { entry, balance: running };
      running -= entry.units;
      return row;
    });
  }, [contingent.entries, contingent.unitBalance]);

  return (
    <div className="space-y-4 rounded-md border bg-muted/20 p-3">
      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <div className="text-sm text-muted-foreground">{usageBased ? 'Einheiten' : 'Resteinheiten'}</div>
          <UnitBalance units={contingent.unitBalance} usageBased={usageBased} />
          {closed && (
            <div className="text-xs text-muted-foreground">Zum Vertragsende abgeschlossen am {formatIsoDate(contingent.closedAt)}</div>
          )}
        </div>

        {!usageBased && (
          <div className="space-y-2">
            <Label htmlFor={`followup-${detail.id}`}>Folgepaket</Label>
            {canEdit && !closed ? (
              <Select
                value={contingent.followupTariffId ? String(contingent.followupTariffId) : NO_FOLLOWUP}
                onValueChange={(value) =>
                  void onRun(
                    () =>
                      billingApi.updateContingent(detail.id, {
                        followupTariffId: value === NO_FOLLOWUP ? null : Number(value),
                      }),
                    'Folgepaket gespeichert',
                  )
                }
                disabled={busy}
              >
                <SelectTrigger id={`followup-${detail.id}`}>
                  <SelectValue placeholder="Paket wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_FOLLOWUP}>Paket aus der Anmeldung</SelectItem>
                  {contingent.followupTariffId && !packageTariffs.some((t) => t.id === contingent.followupTariffId) && (
                    <SelectItem value={String(contingent.followupTariffId)} disabled>
                      {followupName ?? `Tarif #${contingent.followupTariffId}`} (nicht mehr buchbar)
                    </SelectItem>
                  )}
                  {packageTariffs.map((tariff) => (
                    <SelectItem key={tariff.id} value={String(tariff.id)}>
                      {tariff.name} · {tariffPriceLabel(tariff)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div id={`followup-${detail.id}`} className="text-sm">
                {followupName ?? 'Paket aus der Anmeldung'}
              </div>
            )}
          </div>
        )}

        {!usageBased && (
          <div className="space-y-2">
            <div className="text-sm font-medium">Automatisch verlängern</div>
            <label className="flex items-center gap-3 text-sm">
              <Switch
                checked={contingent.autoRenew}
                disabled={!canEdit || busy || closed}
                onCheckedChange={(checked) =>
                  void onRun(
                    () => billingApi.updateContingent(detail.id, { autoRenew: checked }),
                    checked ? 'Folgepaket wird automatisch gebucht' : 'Automatische Verlängerung aus',
                  )
                }
              />
              {contingent.autoRenew ? 'Folgepaket bei 0 Einheiten' : 'Aus – nur manuell nachbuchen'}
            </label>
            {detail.serviceEnd && (
              <p className="text-xs text-muted-foreground">
                Vertrag endet am {formatIsoDate(detail.serviceEnd)} – es wird kein Folgepaket mehr gebucht.
              </p>
            )}
          </div>
        )}
      </div>

      {canEdit && !closed && (
        <div className="flex flex-wrap gap-2">
          {!usageBased && (
            <Button variant="outline" size="sm" onClick={() => setBuying(true)} disabled={busy || packageTariffs.length === 0}>
              <PackagePlus className="mr-2 h-4 w-4" />
              Paket nachbuchen
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setAdjusting(true)} disabled={busy}>
            <SlidersHorizontal className="mr-2 h-4 w-4" />
            Einheiten korrigieren
          </Button>
        </div>
      )}

      {contingent.packages.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Pakete</h4>
          <div className="space-y-2">
            {contingent.packages.map((pkg) => (
              <PackageRow key={pkg.id} pkg={pkg} />
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <h4 className="text-sm font-medium">Einheitenkonto</h4>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch keine Einheiten gebucht.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border bg-background">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Datum</TableHead>
                  <TableHead>Buchung</TableHead>
                  <TableHead className="text-right">Einheiten</TableHead>
                  <TableHead className="text-right">Stand</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.slice(0, entryLimit).map(({ entry, balance }) => (
                  <TableRow key={entry.id}>
                    <TableCell className="whitespace-nowrap tabular-nums">
                      {formatIsoDate(
                        entry.lessonDate ??
                          (entry.packageId ? purchasedOn.get(entry.packageId) : undefined) ??
                          entry.createdAt?.slice(0, 10) ??
                          null,
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{UNIT_ENTRY_KIND_LABEL[entry.kind] ?? entry.kind}</div>
                      <div className="text-xs text-muted-foreground">
                        {entry.description}
                        {entry.lessonStatus && entry.kind !== 'debit' && (
                          <> · Status {LESSON_STATUS_LABEL[entry.lessonStatus] ?? entry.lessonStatus}</>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className={cn('text-right tabular-nums', entry.units > 0 && 'text-success')}>
                      {entry.units > 0 ? `+${entry.units}` : `−${Math.abs(entry.units)}`}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{balance}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {entries.length > entryLimit && (
              <div className="border-t p-2 text-center">
                <Button variant="ghost" size="sm" onClick={() => setEntryLimit((limit) => limit + ENTRY_PAGE)}>
                  Ältere Buchungen zeigen
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {buying && (
        <BuyPackageDialog
          accountNumber={detail.accountNumber}
          tariffs={packageTariffs}
          defaultTariffId={contingent.followupTariffId ?? detail.tariffId}
          busy={busy}
          onClose={() => setBuying(false)}
          onSubmit={async (input) => {
            if (await onRun(() => billingApi.buyPackage(detail.id, input), 'Paket gebucht')) setBuying(false);
          }}
        />
      )}

      {adjusting && (
        <AdjustUnitsDialog
          accountNumber={detail.accountNumber}
          busy={busy}
          onClose={() => setAdjusting(false)}
          onSubmit={async (input) => {
            if (await onRun(() => billingApi.adjustUnits(detail.id, input), 'Einheiten korrigiert')) setAdjusting(false);
          }}
        />
      )}
    </div>
  );
}

function PackageRow({ pkg }: { pkg: ContingentPackage }) {
  const [open, setOpen] = useState(false);
  const posted = pkg.installments.filter((installment) => installment.posted).length;
  return (
    <div className="rounded-md border bg-background p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <span className="font-medium">{pkg.name}</span>
          <span className="text-muted-foreground">
            {' '}
            · {pkg.units} Einheiten · {formatCents(pkg.priceCents)} · gebucht {formatIsoDate(pkg.purchasedOn)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" size="sm">
            {PACKAGE_SOURCE_LABEL[pkg.source] ?? pkg.source}
          </Badge>
          {pkg.paymentMode === 'installments' ? (
            <button
              type="button"
              className="text-xs text-primary underline-offset-2 hover:underline"
              onClick={() => setOpen((value) => !value)}
              aria-expanded={open}
            >
              Raten {posted}/{pkg.installmentCount} gebucht
            </button>
          ) : (
            <Badge variant="outline" size="sm">
              {CONTINGENT_MODE_LABEL.prepaid}
            </Badge>
          )}
        </div>
      </div>
      {open && pkg.installments.length > 0 && (
        <ul className="mt-2 grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
          {pkg.installments.map((installment) => (
            <li key={installment.number} className="flex justify-between gap-2 rounded bg-muted/40 px-2 py-1 tabular-nums">
              <span>
                {installment.number}. Rate · {formatIsoDate(installment.dueDate)}
              </span>
              <span className={cn(!installment.posted && 'text-muted-foreground')}>
                {formatCents(installment.amountCents)}
                {installment.posted ? '' : ' (offen)'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface BuyPackageDialogProps {
  accountNumber: string;
  tariffs: Tariff[];
  defaultTariffId: number | null;
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: { tariffId: number; purchasedOn: string; idempotencyKey: string }) => Promise<void>;
}

function BuyPackageDialog({ accountNumber, tariffs, defaultTariffId, busy, onClose, onSubmit }: BuyPackageDialogProps) {
  const initial = tariffs.some((tariff) => tariff.id === defaultTariffId) ? defaultTariffId : tariffs[0]?.id;
  const [tariffId, setTariffId] = useState(initial ? String(initial) : '');
  const [purchasedOn, setPurchasedOn] = useState(todayIso());
  // Ein Schlüssel je Dialog: ein Doppelklick bucht das Paket nicht zweimal.
  const [idempotencyKey] = useState(newIdempotencyKey);
  const tariff = tariffs.find((entry) => String(entry.id) === tariffId);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Paket nachbuchen</DialogTitle>
          <DialogDescription>
            Vertragskonto {accountNumber}. Die Einheiten stehen sofort zur Verfügung; der Preis wird als Forderung
            gebucht und mit dem nächsten Zahlungslauf eingezogen.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="package-tariff">Paket</Label>
            <Select value={tariffId} onValueChange={setTariffId}>
              <SelectTrigger id="package-tariff">
                <SelectValue placeholder="Paket wählen" />
              </SelectTrigger>
              <SelectContent>
                {tariffs.map((entry) => (
                  <SelectItem key={entry.id} value={String(entry.id)}>
                    {entry.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {tariff && (
              <p className="text-sm text-muted-foreground">
                {tariffPriceLabel(tariff)}
                {tariff.contingentMode === 'installments' && tariff.installmentCount
                  ? ' – erste Rate am Buchungstag, danach monatlich'
                  : ''}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="package-date">Gebucht am</Label>
            <Input id="package-date" type="date" value={purchasedOn} onChange={(event) => setPurchasedOn(event.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            onClick={() => void onSubmit({ tariffId: Number(tariffId), purchasedOn, idempotencyKey })}
            disabled={busy || !tariff || !purchasedOn}
          >
            Paket buchen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface AdjustUnitsDialogProps {
  accountNumber: string;
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: { units: number; description: string; idempotencyKey: string }) => Promise<void>;
}

function AdjustUnitsDialog({ accountNumber, busy, onClose, onSubmit }: AdjustUnitsDialogProps) {
  const [direction, setDirection] = useState<'credit' | 'debit'>('credit');
  const [units, setUnits] = useState('1');
  const [description, setDescription] = useState('');
  const [idempotencyKey] = useState(newIdempotencyKey);
  const count = Number(units);
  const valid = Number.isInteger(count) && count > 0 && count <= 500;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Einheiten korrigieren</DialogTitle>
          <DialogDescription>
            Vertragskonto {accountNumber}. Nur Einheiten, kein Geld – für Beträge eine Buchung erfassen. Korrekturen
            bleiben im Einheitenkonto sichtbar.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="adjust-direction">Art</Label>
              <Select value={direction} onValueChange={(value) => setDirection(value as 'credit' | 'debit')}>
                <SelectTrigger id="adjust-direction">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="credit">Gutschreiben</SelectItem>
                  <SelectItem value="debit">Abziehen</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="adjust-units">Einheiten</Label>
              <Input
                id="adjust-units"
                type="number"
                min={1}
                max={500}
                value={units}
                onChange={(event) => setUnits(event.target.value)}
                aria-invalid={!valid}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="adjust-description">Begründung</Label>
            <Input
              id="adjust-description"
              maxLength={160}
              placeholder="z. B. Kulanz Lehrerausfall, Übernahme Altsystem"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            onClick={() =>
              void onSubmit({
                units: direction === 'credit' ? count : -count,
                description: description.trim(),
                idempotencyKey,
              })
            }
            disabled={busy || !valid || !description.trim()}
          >
            Buchen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
